/**
 * Export Request Service
 *
 * Handles building export API URLs, cURL commands, endpoints, and request bodies
 */

import type { SymbolKey } from '@/entities/symbol';
import type { ExportRequestBody } from '@/features/DataHub/api/export.api';

export type ExportRequestBaseParams = {
  gxURL: string;
  symbols: SymbolKey[];
  fromValue: string;
  toValue: string | null;
  includeMetadata: boolean;
  userTimezone: string;
};

export type ExportCurlRequestParams = ExportRequestBaseParams & {
  apiKey: string;
};

export type ExportEndpointParams = {
  type: string;
  fromValue: string;
  toValue: string | null;
  includeMetadata: boolean;
  timezone: string;
  dateFormat: string;
};

export class ExportRequestService {
  /**
   * Build export API request string (URL)
   */
  buildApiRequestString(params: ExportRequestBaseParams): string {
    return this.buildApiUrl(params);
  }

  /**
   * Build cURL command string for export API
   */
  buildCurlRequestString(params: ExportCurlRequestParams): string {
    const url = this.buildApiUrl(params);
    return `curl --location "${url}" --header "Accept: application/json" --header "Authorization: ApiKey ${params.apiKey}"`;
  }

  /**
   * Build export endpoint path with query parameters
   */
  buildEndpoint(params: ExportEndpointParams): string {
    const { type, fromValue, toValue, includeMetadata, timezone, dateFormat } = params;

    const urlType = type.startsWith('excel') ? type : `third-party?type=${type}`;
    const toParam = toValue ? `&to=${toValue}` : '';
    return `export/${urlType}&from=${fromValue}${toParam}&metadata=${includeMetadata}&timezone=${timezone}&dateFormat=${dateFormat}`;
  }

  /**
   * Build export request body from symbols
   */
  buildBody(symbols: SymbolKey[], group: string): ExportRequestBody {
    return {
      symbols: symbols.map(symbol => ({
        group,
        key: {
          code: symbol.Code,
          periodType: symbol.PeriodType,
          timeRef: symbol.TimeRef,
          period: symbol.Period
        }
      }))
    };
  }

  /**
   * Build the base export API URL with query parameters
   */
  private buildApiUrl(params: ExportRequestBaseParams): string {
    const { gxURL, symbols, fromValue, toValue, includeMetadata, userTimezone } = params;

    const codes = [...new Set(symbols.map(symbol => symbol.Code))].join(',') || '';
    const urlParams = new URLSearchParams();
    urlParams.set('code', codes);
    urlParams.set('from', fromValue);
    if (toValue) {
      urlParams.set('to', toValue);
    }
    urlParams.set('metadata', String(includeMetadata));
    urlParams.set('timezone', userTimezone);

    return `${gxURL}/index?${urlParams.toString()}`;
  }
}

// Export singleton instance
export const exportRequestService = new ExportRequestService();

// Legacy function exports for backwards compatibility
export const buildExportApiRequestString = (params: ExportRequestBaseParams) =>
  exportRequestService.buildApiRequestString(params);

export const buildExportCurlRequestString = (params: ExportCurlRequestParams) =>
  exportRequestService.buildCurlRequestString(params);

export const buildExportEndpoint = (params: ExportEndpointParams) =>
  exportRequestService.buildEndpoint(params);

export const buildExportBody = (symbols: SymbolKey[], group: string) =>
  exportRequestService.buildBody(symbols, group);
